/** Compiling inline markup into plain DOM, for the two places that cannot use
 *  the tree-driven decoration layer: a rendered table cell (the whole table is
 *  one block replacement, so the layer cannot reach inside it) and the editable
 *  grid's display state.
 *
 *  It lives in its own module to keep `live-preview.ts` and `editable-table.ts`
 *  from importing each other — they already form a cycle through `parseTable`,
 *  and hanging more runtime-critical code off it is asking for a
 *  partially-initialised module at load time. */

/** The inline markup a rendered table cell compiles for itself. The whole table
 *  is ONE block replacement, so the tree-driven inline layer cannot reach
 *  inside it: without this a cell would show its literal `**markers**` while
 *  every other line of the note — and the read-only Preview — compiles them.
 *
 *  Order matters twice. Code spans come first, so a marker inside one stays
 *  literal (parity with the tree, where `` `==x==` `` is left alone); the
 *  single-character emphasis rules come last, so they never steal the first `*`
 *  of a `**`. Elements carry exactly the classes the inline layer uses, so one
 *  set of CSS rules styles both paths. */
interface CellRule {
  re: RegExp;
  tag: string;
  cls: string;
  /** Content is literal — don't recurse (code spans). */
  raw?: boolean;
  /** `[[target|alias]]`: show the alias half, as the inline layer does. */
  alias?: boolean;
  /** Refuse to open mid-word. Only the `_` forms need it, and they need it
   *  badly: without it `snake_case_name` in a cell renders as an italic. */
  wordSafe?: boolean;
  /** `![alt](src)` — emits a real picture rather than styled text. Capture 1 is
   *  the alt text, capture 2 the source. */
  image?: boolean;
}

// Every paired marker refuses to hug a space (`2 * 3 * 4` is arithmetic, not
// emphasis) — the same rule CommonMark and `remarkHighlight` apply. `mark` is
// already regex-escaped, which also holds inside the negated class.
const PAIRED = (mark: string) => new RegExp(`^${mark}(\\S(?:[^${mark}]*\\S)?)${mark}`);

const CELL_RULES: CellRule[] = [
  { re: /^`([^`]+)`/, tag: "code", cls: "cm-code", raw: true },
  // BEFORE the link rule: `![alt](src)` starts with a `[…](…)` the link rule
  // would otherwise claim, rendering the picture as its alt text with a stray
  // `!` left in front of it.
  { re: /^!\[([^\]]*)\]\(([^)\s]+)\)/, tag: "span", cls: "cm-img", image: true },
  { re: PAIRED("\\*\\*"), tag: "strong", cls: "cm-strong" },
  { re: PAIRED("__"), tag: "strong", cls: "cm-strong", wordSafe: true },
  { re: PAIRED("~~"), tag: "del", cls: "cm-strike" },
  { re: PAIRED("=="), tag: "mark", cls: "cm-highlight" },
  { re: /^\[\[([^\]]+)\]\]/, tag: "span", cls: "cm-wikilink", alias: true },
  // A link renders as its styled TEXT, not as an `<a>`: a live anchor sitting
  // in the writing surface would navigate the studio away on a stray click,
  // and Live doesn't make links clickable anywhere else either.
  { re: /^\[([^\]]*)\]\([^)\s]*\)/, tag: "span", cls: "cm-link" },
  { re: PAIRED("\\*"), tag: "em", cls: "cm-em" },
  { re: PAIRED("_"), tag: "em", cls: "cm-em", wordSafe: true },
];

/** Compile `text` into `into`. Recursive, so `**bold [[link]]**` still works;
 *  every character reaches the DOM as a text node or `textContent`, so a cell
 *  can never inject markup. Cells are a few dozen characters, which is what
 *  makes the rescan-per-character shape affordable and readable. Exported for
 *  the DOM unit tests. */
export function renderCellInline(text: string, into: HTMLElement): void {
  let plain = "";
  const flush = () => {
    if (plain) into.appendChild(document.createTextNode(plain));
    plain = "";
  };
  const midWord = (i: number) => i > 0 && /\w/.test(text[i - 1] ?? "");
  for (let i = 0; i < text.length; ) {
    const rest = text.slice(i);
    let hit: { rule: CellRule; m: RegExpExecArray } | null = null;
    for (const rule of CELL_RULES) {
      if (rule.wordSafe && midWord(i)) continue;
      const m = rule.re.exec(rest);
      if (m) {
        hit = { rule, m };
        break;
      }
    }
    if (!hit) {
      plain += text[i];
      i += 1;
      continue;
    }
    flush();
    const el = into.appendChild(document.createElement(hit.rule.tag));
    el.className = hit.rule.cls;
    const inner = hit.m[1] ?? "";
    if (hit.rule.image) {
      const img = el.appendChild(document.createElement("img"));
      img.src = hit.m[2] ?? "";
      img.alt = inner;
      img.decoding = "async";
      img.onerror = () => {
        img.remove();
        el.classList.add("cm-img--broken");
        el.textContent = `🖼 ${inner || (hit.m[2] ?? "")}`;
      };
      i += hit.m[0].length;
      continue;
    }
    // `[[target|alias]]` shows the alias, matching what the inline layer leaves
    // visible when it hides the "target|" prefix.
    if (hit.rule.raw) el.textContent = inner;
    else renderCellInline(hit.rule.alias ? (inner.split("|").pop() ?? inner) : inner, el);
    i += hit.m[0].length;
  }
  flush();
}
